import { Component } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

export class AppErrorBoundary extends Component {
  state = { error: null, lastResetKey: undefined };
  // Clear a caught error when the route changes — WITHOUT key-remounting the
  // whole subtree (that reset sidebar scroll + refetched the shell every nav).
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.lastResetKey) {
      return { error: null, lastResetKey: props.resetKey };
    }
    return null;
  }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('[page-error]', error, info); }
  retry = () => this.setState({ error: null });
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[60vh] grid place-items-center px-6" data-testid="page-error">
        <div className="text-center max-w-sm">
          <div className="h-12 w-12 rounded-2xl bg-secondary grid place-items-center mx-auto mb-4"><RefreshCw className="h-5 w-5 text-muted-foreground" /></div>
          <h2 className="font-display text-2xl">This page couldn’t load.</h2>
          <p className="text-sm text-muted-foreground mt-2">A network blip or an app update got in the way — it’s not you. Try again.</p>
          <div className="flex items-center justify-center gap-2 mt-5">
            <Button variant="outline" className="rounded-full" onClick={this.retry} data-testid="page-error-retry">Try again</Button>
            <Button className="rounded-full" onClick={() => window.location.reload()} data-testid="page-error-reload">Reload app</Button>
          </div>
        </div>
      </div>
    );
  }
}

/** Boundary that auto-resets when the route changes, so one bad page never traps the user. */
export const RouteErrorBoundary = ({ children }) => {
  const { pathname } = useLocation();
  return <AppErrorBoundary resetKey={pathname}>{children}</AppErrorBoundary>;
};

export default AppErrorBoundary;
